import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: string | string[];  // Can be single role or array of allowed roles
  viewerAllowed?: boolean;  // Shorthand for allowing viewers
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireRole,
  viewerAllowed = false
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = user?.role_name?.toLowerCase() || '';
  const isAdmin = userRole === 'admin';
  const isViewer = userRole === 'viewer';
  if (userRole === 'staff' && location.pathname !== '/staff') return <Navigate to="/staff" replace />;

  // VIEWER RESTRICTION: Check first before any other logic
  // Viewers can ONLY access specific pages - redirect all others immediately
  if (isViewer && !viewerAllowed) {
    const viewerAllowedPaths = [
      '/prepare/upcoming-services',
      '/choir/upcoming-services',
    ];

    const isAllowedPath = viewerAllowedPaths.some(path =>
      location.pathname === path || location.pathname.startsWith(path + '/')
    );

    if (!isAllowedPath) {
      return <Navigate to="/prepare/upcoming-services" replace />;
    }
  }

  // Check role-based access if required (for non-viewers)
  if (requireRole && !isViewer) {
    const allowedRoles = Array.isArray(requireRole)
      ? requireRole.map(r => r.toLowerCase())
      : [requireRole.toLowerCase()];

    // Admin always has access
    if (!isAdmin && !allowedRoles.includes(userRole)) {
      return (
        <div className="container mt-5">
          <div className="alert alert-danger" role="alert">
            <h4>Access Denied</h4>
            <p>You don't have permission to access this page.</p>
            <p>Your role: {user?.role_name || 'Unknown'}</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
